import { useCallback, useEffect, useState } from "react";
import { api, ApiError, json } from "./api";
import { Auth } from "./Auth";
import { Projects } from "./Projects";
import { Workspace } from "./Workspace";
import { Brand, Spinner } from "./components";
import type { Project, User } from "./types";

export function App() {
  const [user, setUser] = useState<User | null>(null),
    [setup, setSetup] = useState(false),
    [allowed, setAllowed] = useState(false),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [project, setProject] = useState<Project | null>(null),
    [initialDocument, setInitialDocument] = useState<string | null>(null);
  useEffect(() => {
    api<{
      user: User | null;
      setupRequired: boolean;
      webSetupAllowed: boolean;
    }>("/session")
      .then(async (session) => {
        setUser(session.user);
        setSetup(session.setupRequired);
        setAllowed(session.webSetupAllowed);
        const params = new URLSearchParams(location.search);
        if (session.user && params.get("project")) {
          try {
            setProject(
              await api<Project>(
                "/projects/" + encodeURIComponent(params.get("project")!),
              ),
            );
            setInitialDocument(params.get("document"));
          } catch (e) {
            setError((e as Error).message);
            history.replaceState(null, "", "/");
          }
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);
  const onDocument = useCallback((id: string | null) => {
    const params = new URLSearchParams(location.search);
    if (id) params.set("document", id);
    else params.delete("document");
    if (id) {
      params.delete("view");
      params.delete("board");
      params.delete("entity");
    }
    history.replaceState(null, "", "/?" + params);
  }, []);
  async function logout() {
    try {
      await api("/auth/logout", json("POST", {}));
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 401)) {
        setError((e as Error).message);
        return;
      }
    }
    setUser(null);
    setProject(null);
    setInitialDocument(null);
    history.replaceState(null, "", "/");
  }
  function open(p: Project) {
    setProject(p);
    setInitialDocument(null);
    history.replaceState(null, "", "/?project=" + p.id);
  }
  function back() {
    setProject(null);
    setInitialDocument(null);
    history.replaceState(null, "", "/");
  }
  if (loading)
    return (
      <div className="app-loading">
        <Brand />
        <Spinner />
      </div>
    );
  if (!user)
    return (
      <>
        {error && (
          <div className="fatal-error" role="alert">
            {error}
            <button onClick={() => location.reload()}>Повторить</button>
          </div>
        )}
        <Auth
          setup={setup}
          allowed={allowed}
          onLogin={(u) => {
            setUser(u);
            setSetup(false);
            setError("");
          }}
        />
      </>
    );
  return (
    <>
      {error && (
        <div className="fatal-error" role="alert">
          {error}
          <button onClick={() => setError("")}>Закрыть</button>
        </div>
      )}
      {project ? (
        <Workspace
          key={project.id}
          project={project}
          user={user}
          initialDocument={initialDocument}
          back={back}
          logout={() => void logout()}
          onProject={setProject}
          onDocument={onDocument}
        />
      ) : (
        <Projects user={user} open={open} logout={() => void logout()} />
      )}
    </>
  );
}
